/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchOpeningPuzzles } from "../api/fetchOpeningPuzzles";
import OpeningPuzzle from "./OpeningPuzzle";
import { markCompleted } from "../api/openingPuzzles/markCompleted";
import { getCompleted } from "../api/openingPuzzles/getCompleted";


// Sort key constants
const SORT_RATING_ASC  = 'rating_asc';
const SORT_RATING_DESC = 'rating_desc';
const SORT_NAME_ASC    = 'name_asc';
const SORT_NAME_DESC   = 'name_desc';

const SORT_LABELS = {
    [SORT_RATING_ASC]:  'Rating ↑',
    [SORT_RATING_DESC]: 'Rating ↓',
    [SORT_NAME_ASC]:    'Name A→Z',
    [SORT_NAME_DESC]:   'Name Z→A',
};

function sortPuzzles(puzzles, sortKey) {
    return [...puzzles].sort((a, b) => {
        switch (sortKey) {
            case SORT_RATING_ASC:  return a.Rating - b.Rating;
            case SORT_RATING_DESC: return b.Rating - a.Rating;
            case SORT_NAME_ASC:    return a.Opening.localeCompare(b.Opening);
            case SORT_NAME_DESC:   return b.Opening.localeCompare(a.Opening);
            default:               return 0;
        }
    });
}

function CheckIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-success"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
        >
            <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
}

function PuzzleCard({ puzzle, isCompleted, onClick }) {
    return (
        <button
            onClick={() => onClick(puzzle)}
            className={[
                'card bg-base-200 shadow hover:shadow-lg transition-shadow cursor-pointer text-left w-72',
                isCompleted ? 'opacity-60 border border-success/30' : 'border border-base-300',
            ].join(' ')}
        >
            <div className="card-body p-4 gap-2">
                <div className="flex items-start justify-between gap-2">
                    <h2 className="card-title text-base leading-snug">{puzzle.Opening}</h2>
                    {isCompleted && <CheckIcon />}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <span className="badge badge-outline badge-sm text-primary border-primary">
                        ELO {puzzle.Rating}
                    </span>
                    {isCompleted && (
                        <span className="badge badge-success badge-sm gap-1">
                            Completed
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

function SectionHeader({ children }) {
    return (
        <div className="w-full px-2">
            <p className="text-xs uppercase tracking-widest text-gray-500 mt-6 mb-2">
                {children}
            </p>
            <div className="divider my-0" />
        </div>
    );
}

function OpeningPuzzles({ userData }) {
    const [puzzles, setPuzzles] = useState(null);
    const [puzzleSelectionMode, setPuzzleSelectionMode] = useState(true);
    const [chosenPuzzle, setChosenPuzzle] = useState(null);
    const [completed, setCompleted] = useState([]);

    const [sortKey, setSortKey] = useState(SORT_RATING_ASC);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchOpeningPuzzles(setPuzzles);
        getCompleted(userData.id, setCompleted);
    }, []);

    const selectPuzzle = (puzzle) => {
        setChosenPuzzle(puzzle);
        setPuzzleSelectionMode(false);
    };

    const puzzleCompleted = useCallback((puzzleId) => {
        const newCompleted = [...completed, puzzleId];
        markCompleted(userData.id, puzzleId);
        setCompleted(newCompleted);
        // Navigation handled by success screen in OpeningPuzzle
    }, [userData, completed]);

    const nextPuzzle = useCallback(() => {
        if (!puzzles) { setPuzzleSelectionMode(true); return; }
        const next = puzzles.find(p => p.id !== chosenPuzzle?.id && !completed.includes(p.id));
        if (next) {
            setChosenPuzzle(next);
        } else {
            setPuzzleSelectionMode(true);
        }
    }, [puzzles, chosenPuzzle, completed]);

    // Derived: filter → sort → split into available / completed sections
    const { available, completedPuzzles } = useMemo(() => {
        if (!puzzles) return { available: [], completedPuzzles: [] };

        const needle = search.trim().toLowerCase();
        const filtered = needle
            ? puzzles.filter(p => p.Opening.toLowerCase().includes(needle))
            : puzzles;

        const sorted = sortPuzzles(filtered, sortKey);

        return {
            available:        sorted.filter(p => !completed.includes(p.id)),
            completedPuzzles: sorted.filter(p =>  completed.includes(p.id)),
        };
    }, [puzzles, completed, sortKey, search]);

    if (!puzzleSelectionMode) {
        return (
            <OpeningPuzzle
                key={chosenPuzzle?.id}
                puzzle={chosenPuzzle}
                puzzleCompleted={puzzleCompleted}
                setPuzzleSelectionMode={setPuzzleSelectionMode}
                onNextPuzzle={nextPuzzle}
            />
        );
    }

    return (
        <div style={styles.container}>
            <h1 className="text-center text-4xl font-bold text-primary" style={{ marginTop: '6vh', paddingBottom: '3vh' }}>
                Opening Puzzles
            </h1>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-center gap-3 px-4 mb-4 max-w-4xl mx-auto w-full">
                {/* Search */}
                <input
                    type="text"
                    placeholder="Search opening…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="input input-sm input-bordered w-full sm:w-56"
                />

                {/* Sort buttons */}
                <div className="flex items-center gap-2 flex-wrap justify-center">
                    <span className="text-xs text-gray-500 whitespace-nowrap">Sort by:</span>
                    {Object.entries(SORT_LABELS).map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setSortKey(key)}
                            className={`btn btn-sm ${sortKey === key ? 'btn-primary' : ''}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Puzzle grid */}
            <div className="px-4 max-w-6xl mx-auto w-full">

                {/* Available section */}
                {available.length > 0 && (
                    <>
                        <SectionHeader>Available Puzzles ({available.length})</SectionHeader>
                        <div className="flex flex-row flex-wrap justify-evenly gap-4 mt-4">
                            {available.map(puzzle => (
                                <PuzzleCard
                                    key={puzzle.id}
                                    puzzle={puzzle}
                                    isCompleted={false}
                                    onClick={selectPuzzle}
                                />
                            ))}
                        </div>
                    </>
                )}

                {available.length === 0 && puzzles && (
                    <p className="text-center text-gray-500 mt-8">No available puzzles match your search.</p>
                )}

                {/* Completed section */}
                {completedPuzzles.length > 0 && (
                    <>
                        <SectionHeader>Completed Puzzles ({completedPuzzles.length})</SectionHeader>
                        <div className="flex flex-row flex-wrap justify-evenly gap-4 mt-4 mb-12">
                            {completedPuzzles.map(puzzle => (
                                <PuzzleCard
                                    key={puzzle.id}
                                    puzzle={puzzle}
                                    isCompleted={true}
                                    onClick={selectPuzzle}
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* Loading state */}
                {!puzzles && (
                    <div className="flex justify-center mt-16">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                )}
            </div>
        </div>
    );
}


const styles = {
    container: {
        fontFamily: "Raleway, sans-serif",
        height: '100%',
        width: '100%',
    },
};

export default OpeningPuzzles;
